import androidx.compose.foundation.background
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.LocalMinimumInteractiveComponentSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.remove_ads
import org.jetbrains.compose.resources.stringResource

/**
 * The bottom ad slot: the AdMob banner with a contextual "Remove ads" tap directly above it — the
 * highest-intent place to offer going ad-free, since the player is looking at the ad they'd remove.
 * Tapping opens the store selector (so they can pick the cheaper Plus subscription, not just the
 * one-time buy). Hidden entirely once ads are gone (own purchase or a Plus host's lobby). The tap is
 * only shown where purchases are actually possible (Android/iOS), so web/desktop just show the banner.
 */
@Composable
fun RemoveAdsBanner() {
    val purchaseState by RemoveAdsPurchases.uiState.collectAsState()
    val lobbyAdsDisabled by LobbyAdState.adsDisabledForLobby.collectAsState()
    // Plus owners have nothing left to buy — show nothing.
    if (purchaseState.bankoPlusActive) return

    // Only the ad itself earns this slot: nothing shows (and nothing is reserved) until a creative
    // has actually loaded, and the "Remove ads" link appears only above a real ad. Ads gone (own
    // purchase or a Plus host's lobby) means no slot at all; the store stays in Settings.
    val adsGone = purchaseState.adsRemoved || lobbyAdsDisabled
    val entitlementsReadyForAds = purchaseState.entitlementsResolved || !purchaseState.isPurchasesSupported
    if (adsGone || !entitlementsReadyForAds) return

    var showStore by remember { mutableStateOf(false) }
    var adLoaded by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(color = if (adLoaded) MaterialTheme.colorScheme.background else Color.Transparent)
            .then(if (adLoaded) Modifier.navigationBarsPadding() else Modifier),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        if (purchaseState.isPurchasesSupported && adLoaded) {
            // Opt out of the 48dp minimum-touch-target inflation: as a TextButton this chin doubled
            // in height vs the plain clickable Text it replaced. Kept to the old ~28dp footprint so
            // the ad slot doesn't eat more of the game screen than the ad itself requires.
            CompositionLocalProvider(LocalMinimumInteractiveComponentSize provides Dp.Unspecified) {
                TextButton(
                    onClick = {
                        showStore = true
                    },
                    enabled = purchaseState.isConfigured && !purchaseState.isLoading,
                    modifier = Modifier.height(28.dp),
                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 2.dp),
                ) {
                    Text(
                        text = stringResource(Res.string.remove_ads),
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        }
        BoxWithConstraints(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center,
        ) {
            val adWidth = if (maxWidth < 320.dp) maxWidth else 320.dp
            AdMobIntegration(
                modifier = Modifier.width(adWidth),
                onLoadedChange = { adLoaded = it },
            )
        }
    }

    if (showStore) {
        BankoStoreSheet(onDismiss = { showStore = false })
    }
}
